import {
  ListItemText,
  Typography,
  Box,
  Skeleton,
  Tooltip,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import { getCareMatrixModuleTopicsPage } from 'services/user';

import List from '../List';

import * as S from './styles';

interface TopicProps {
  open: boolean;
  cognitoId: string;
  topicId: number;
}

export function Topic({ open, cognitoId, topicId }: TopicProps) {
  const intl = useIntl();
  const { data: pages, isLoading } = useQuery({
    queryFn: () => getCareMatrixModuleTopicsPage(cognitoId, topicId),
    queryKey: ['pages', topicId],
    enabled: open,
  });

  return (
    <List.Root>
      {isLoading &&
        Array.from({ length: 2 }).map((_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <List.Item key={index}>
            <Box display="flex" alignItems="center" gap={2} width="100%">
              <ListItemText>
                <Box display="flex" flexDirection="column" width="100%">
                  <Skeleton width="100%" height={50} variant="text" />
                  <Box display="flex" gap={2}>
                    <Typography variant="body2" color="text.secondary">
                      <Skeleton width={40} height={20} variant="text" />
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <Skeleton width={20} height={20} variant="text" />
                    </Typography>
                  </Box>
                </Box>
              </ListItemText>
            </Box>
          </List.Item>
        ))}

      {pages &&
        pages.map(page => {
          return (
            <List.Item key={page.id}>
              <Box display="flex" alignItems="center" gap={2}>
                {page.acknowledge && page.acknowledge_date && (
                  <Tooltip
                    placement="top"
                    arrow
                    title={intl.formatMessage(
                      { id: 'CAREMATRIXCPTC.PROGRAMCMTOOLTIP.DONE' },
                      {
                        date: new Date(
                          page?.acknowledge_date,
                        ).toLocaleDateString(intl.locale, {
                          day: '2-digit',
                          month: '2-digit',
                          year: '2-digit',
                        }),
                      },
                    )}
                  >
                    <S.CheckIcon />
                  </Tooltip>
                )}
                <ListItemText>
                  <Box display="flex" flexDirection="column">
                    <S.Link
                      href={`/new-provider/content-library/programs/${page.program_id}/pages/${page.id}`}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {page.title}
                    </S.Link>
                    <Box display="flex" gap={2}>
                      <Typography variant="body2" color="text.secondary">
                        {page.type === 'article' &&
                          intl.formatMessage({
                            id: 'CAREMATRIXCPTC.ACTIVITIESTAB.ARTICLETYPE',
                          })}

                        {page.type === 'meditation' &&
                          intl.formatMessage({
                            id: 'CAREMATRIXCPTC.ACTIVITIESTAB.MEDITATIONTYPE',
                          })}

                        {page.type === 'music' &&
                          intl.formatMessage({
                            id: 'CAREMATRIXCPTC.ACTIVITIESTAB.MUSICTYPE',
                          })}

                        {page.type === 'video' &&
                          intl.formatMessage({
                            id: 'CAREMATRIXCPTC.ACTIVITIESTAB.VIDEOTYPE',
                          })}

                        {page.type === 'audio' &&
                          intl.formatMessage({
                            id: 'CAREMATRIXCPTC.ACTIVITIESTAB.AUDIOTYPE',
                          })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {page.read_time}
                      </Typography>
                    </Box>
                  </Box>
                </ListItemText>
              </Box>
            </List.Item>
          );
        })}
    </List.Root>
  );
}
